import { useCallback, type ReactNode } from "react";
import { listTrainingSessionDatasets, type TrainingSessionDatasetItem } from "@/api/training";
import {
  MultiDatasetView,
  useMultiDatasetViewController,
  type MultiDatasetPageQuery,
  type MultiDatasetViewController,
  type MultiDatasetViewItem,
} from "@/components/dataset/multiView";
import { submittedDatasetLabel, submittedDatasetUsage } from "../trainingSessionFormat";
import { AnnotationSetMark } from "./AnnotationSetMark";
import styles from "./SessionDatasetsView.module.css";

export type SessionDatasetsController = MultiDatasetViewController<TrainingSessionDatasetItem>;

// 세션 하나의 입력 Dataset 목록 상태. 창 콘텐츠와 세션 상세 창이 이 훅을 부르고 뷰에 넘긴다.
// 훅을 뷰 밖에 두는 이유는 `useMultiDatasetViewController` 에 적었다.
export function useSessionDatasetsController(
  sessionId: string,
  { enabled }: { enabled: boolean },
): SessionDatasetsController {
  const loadPage = useCallback(
    (query: MultiDatasetPageQuery) => listTrainingSessionDatasets(sessionId, query),
    [sessionId],
  );
  return useMultiDatasetViewController({ loadPage, getKey: sessionDatasetKey, enabled });
}

// 세션의 입력 Dataset 을 멀티 데이터셋 뷰로 본다. 줄에는 학습에서의 쓰임(역할·비율·타깃)을 적고,
// 배경 분리에 쓴 어노테이션 세트가 있으면 그 세트로 가는 링크를 줄 옆에 둔다.
export function SessionDatasetsView({
  controller,
}: {
  controller: SessionDatasetsController;
}): ReactNode {
  return (
    <MultiDatasetView
      controller={controller}
      ariaLabel="Session 입력 Dataset"
      describeItem={describeSessionDataset}
      renderItemMeta={submittedDatasetUsage}
      renderItemActions={renderAnnotationSet}
    />
  );
}

// 같은 Dataset 을 비율만 바꿔 두 번 넣을 수 있다. 제출 순서가 한 세션 안에서 유일한 키다.
function sessionDatasetKey(item: TrainingSessionDatasetItem): string {
  return String(item.position);
}

function describeSessionDataset(item: TrainingSessionDatasetItem): MultiDatasetViewItem {
  const ref = item.registry_ref ?? null;
  return {
    dataset:
      ref === null
        ? null
        : { uuid: ref.uuid, displayName: ref.display_name, thumbnailUrl: ref.thumbnail_url },
    fallbackLabel: submittedDatasetLabel(item),
  };
}

function renderAnnotationSet(item: TrainingSessionDatasetItem): ReactNode {
  if ((item.annotation_set_uuid ?? null) === null) {
    return undefined;
  }
  // c6 ListView 는 액션 칸의 span 을 아이콘 칸 폭(2rem)으로 고정한다. 글자 링크라 div 로 감싼다.
  return (
    <div className={styles.annotation}>
      <AnnotationSetMark dataset={item} />
    </div>
  );
}
